import { OnEvent, Service } from '@toeverything/infra';
import type { ManagerOptions, Socket } from 'socket.io-client';
import { Manager } from 'socket.io-client';

import { getPiqueCloudBaseUrl } from '../services/fetch';
import { getCachedTokenSync, refreshTauriAuthToken } from '../utils/tauri-auth';
import { AccountChanged } from './auth';

@OnEvent(AccountChanged, e => e.reconnect)
export class WebSocketService extends Service {
  ioManager: Manager | null = null;
  sockets: Set<Socket> = new Set();
  private pendingTauriTokenRefresh: Promise<void> | null = null;

  constructor() {
    super();
  }

  private createManagerOptions(): Partial<ManagerOptions> {
    return {
      autoConnect: false,
      transports: ['polling', 'websocket'],
      secure: location.protocol === 'https:',
    };
  }

  private applyCachedTauriToken(manager: Manager): boolean {
    if (!environment.isTauri) return true;

    const token = getCachedTokenSync();
    if (!token) return false;

    manager.opts.query = { token };
    return true;
  }

  private refreshTauriTokenForManager(): void {
    if (!environment.isTauri || this.pendingTauriTokenRefresh) return;

    this.pendingTauriTokenRefresh = refreshTauriAuthToken()
      .then(() => {
        const manager = this.ioManager;
        if (!manager || !this.applyCachedTauriToken(manager)) return;

        for (const socket of this.sockets) {
          if (!socket.connected && socket.active) {
            socket.connect();
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        this.pendingTauriTokenRefresh = null;
      });
  }

  private createManager(baseUrl: string): Manager {
    const manager = new Manager(baseUrl, this.createManagerOptions());

    if (environment.isTauri) {
      manager.on('reconnect_attempt', () => {
        this.applyCachedTauriToken(manager);
      });

      if (!this.applyCachedTauriToken(manager)) {
        this.refreshTauriTokenForManager();
      }
    }

    return manager;
  }

  private ensureManager(): Manager {
    if (!this.ioManager) {
      const baseUrl = `${getPiqueCloudBaseUrl()}/`;
      this.ioManager = this.createManager(baseUrl);
    } else if (
      environment.isTauri &&
      !this.applyCachedTauriToken(this.ioManager)
    ) {
      this.refreshTauriTokenForManager();
    }

    return this.ioManager;
  }

  newSocket(): Socket {
    const socket = this.ensureManager().socket('/');
    this.sockets.add(socket);
    return socket;
  }

  reconnect(): void {
    for (const socket of this.sockets) {
      socket.disconnect();
    }

    if (environment.isTauri) {
      // Refresh the vault token and update the Manager query so the next
      // transport handshake carries the fresh token.
      void refreshTauriAuthToken()
        .then(() => {
          if (this.ioManager) {
            this.applyCachedTauriToken(this.ioManager);
          }
          for (const socket of this.sockets) {
            socket.connect();
          }
        })
        .catch(() => {});
      return;
    }

    for (const socket of this.sockets) {
      socket.connect();
    }
  }
}
